import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { APP_VERSION_LABEL } from '../appVersion';
import { API } from '../config';
import { Globe, Moon, Sun, Desktop, Check, HardDrives, CircleNotch } from '@phosphor-icons/react';

const SettingsPage = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();

  const [networkRoot, setNetworkRoot] = useState('');
  const [networkLabel, setNetworkLabel] = useState('');
  const [networkStatus, setNetworkStatus] = useState(null); // null | 'ok' | 'unreachable' | 'unconfigured'
  const [networkSaving, setNetworkSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, statusRes] = await Promise.all([
          axios.get(`${API}/network-files/settings`, { withCredentials: true }),
          axios.get(`${API}/network-files/status`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setNetworkRoot(settingsRes.data?.root || '');
        setNetworkLabel(settingsRes.data?.label || '');
        const st = statusRes.data;
        setNetworkStatus(!st?.configured ? 'unconfigured' : st.available ? 'ok' : 'unreachable');
      } catch {
        /* settings stay empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveNetworkSettings = async () => {
    setNetworkSaving(true);
    try {
      const res = await axios.put(
        `${API}/network-files/settings`,
        { root: networkRoot, label: networkLabel },
        { withCredentials: true }
      );
      const d = res.data;
      setNetworkStatus(!d?.configured ? 'unconfigured' : d.available ? 'ok' : 'unreachable');
      if (d?.configured && !d?.available) {
        toast.warning(t('networkFolderSavedUnreachable'));
      } else {
        toast.success(t('networkFolderSaved'));
      }
    } catch {
      toast.error(t('networkFolderSaveFailed'));
    } finally {
      setNetworkSaving(false);
    }
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  ];

  const themes = [
    { id: 'light', name: t('lightTheme'), icon: Sun },
    { id: 'dark', name: t('darkTheme'), icon: Moon },
    { id: 'system', name: t('systemTheme'), icon: Desktop },
  ];

  return (
    <div data-testid="settings-page" className="animate-fadeIn max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="page-title">{t('settings')}</h1>
        <p className="page-subtitle">{t('settingsDescription')}</p>
      </div>

      {/* Language Section */}
      <div className="vmk-card mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#00CBA9]/10 flex items-center justify-center">
            <Globe size={22} className="text-[#00CBA9]" weight="duotone" />
          </div>
          <div>
            <h2 className="font-semibold text-[#1a1a1a] dark:text-white">{t('language')}</h2>
            <p className="text-sm text-[#64748b]">{t('languageDescription')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                language === lang.code
                  ? 'border-[#00CBA9] bg-[#00CBA9]/5'
                  : 'border-[#e2e8f0] dark:border-[#334155] hover:border-[#00CBA9]/50'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <span className={`font-medium ${language === lang.code ? 'text-[#00CBA9]' : 'text-[#1a1a1a] dark:text-white'}`}>
                {lang.name}
              </span>
              {language === lang.code && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#00CBA9] flex items-center justify-center">
                  <Check size={12} weight="bold" className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Theme Section */}
      <div className="vmk-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#00CBA9]/10 flex items-center justify-center">
            <Sun size={22} className="text-[#00CBA9]" weight="duotone" />
          </div>
          <div>
            <h2 className="font-semibold text-[#1a1a1a] dark:text-white">{t('appearance')}</h2>
            <p className="text-sm text-[#64748b]">{t('appearanceDescription')}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {themes.map((themeOption) => {
            const Icon = themeOption.icon;
            return (
              <button
                key={themeOption.id}
                onClick={() => setTheme(themeOption.id)}
                className={`relative flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  theme === themeOption.id
                    ? 'border-[#00CBA9] bg-[#00CBA9]/5'
                    : 'border-[#e2e8f0] dark:border-[#334155] hover:border-[#00CBA9]/50'
                }`}
                data-testid={`theme-${themeOption.id}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  theme === themeOption.id 
                    ? 'bg-[#00CBA9] text-white' 
                    : 'bg-[#f1f5f9] dark:bg-[#1e293b] text-[#64748b]'
                }`}>
                  <Icon size={24} weight="duotone" />
                </div>
                <span className={`text-sm font-medium ${
                  theme === themeOption.id ? 'text-[#00CBA9]' : 'text-[#1a1a1a] dark:text-white'
                }`}>
                  {themeOption.name}
                </span>
                {theme === themeOption.id && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#00CBA9] flex items-center justify-center">
                    <Check size={12} weight="bold" className="text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Network folder Section */}
      <div className="vmk-card mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#00CBA9]/10 flex items-center justify-center">
            <HardDrives size={22} className="text-[#00CBA9]" weight="duotone" />
          </div>
          <div>
            <h2 className="font-semibold text-[#1a1a1a] dark:text-white">{t('networkFolderTitle')}</h2>
            <p className="text-sm text-[#64748b]">{t('networkFolderDescription')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155] dark:text-[#cbd5e1]">
              {t('networkFolderPathLabel')}
            </label>
            <input
              type="text"
              value={networkRoot}
              onChange={(e) => setNetworkRoot(e.target.value)}
              placeholder={'Z:\\  |  \\\\MARTIJN\\VKMMARTIJN-database files'}
              spellCheck={false}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#1a1a1a] outline-none transition focus:border-[#00CBA9] dark:border-[#334155] dark:bg-[#0f172a] dark:text-white"
              data-testid="network-folder-root-input"
            />
            <p className="mt-1 text-xs text-[#94a3b8]">{t('networkFolderPathHint')}</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155] dark:text-[#cbd5e1]">
              {t('networkFolderLabelLabel')}
            </label>
            <input
              type="text"
              value={networkLabel}
              onChange={(e) => setNetworkLabel(e.target.value)}
              placeholder="VKMMARTIJN-database files (Z:)"
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#1a1a1a] outline-none transition focus:border-[#00CBA9] dark:border-[#334155] dark:bg-[#0f172a] dark:text-white"
              data-testid="network-folder-label-input"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm">
              {networkStatus === 'ok' && (
                <span className="inline-flex items-center gap-1.5 text-emerald-600">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {t('networkFolderStatusOk')}
                </span>
              )}
              {networkStatus === 'unreachable' && (
                <span className="inline-flex items-center gap-1.5 text-amber-600">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  {t('networkFolderStatusUnreachable')}
                </span>
              )}
              {networkStatus === 'unconfigured' && (
                <span className="inline-flex items-center gap-1.5 text-[#94a3b8]">
                  <span className="h-2 w-2 rounded-full bg-[#cbd5e1]" />
                  {t('networkFolderStatusUnconfigured')}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={saveNetworkSettings}
              disabled={networkSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#00CBA9] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#00b598] disabled:opacity-60"
              data-testid="network-folder-save"
            >
              {networkSaving ? <CircleNotch size={16} className="animate-spin" /> : null}
              {t('networkFolderSave')}
            </button>
          </div>

          <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3 dark:border-[#334155] dark:bg-[#0f172a]">
            <p className="text-sm font-medium text-[#334155] dark:text-[#e2e8f0]">{t('networkFolderSyncTitle')}</p>
            <p className="mt-1 text-xs text-[#64748b] leading-relaxed">{t('networkFolderSyncDescription')}</p>
          </div>
        </div>
      </div>

      <p
        className="mt-8 text-center text-sm text-[#64748b] tabular-nums"
        data-testid="settings-app-version"
      >
        {t('appVersion')}: {APP_VERSION_LABEL}
      </p>
    </div>
  );
};

export default SettingsPage;
